import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"
import timezone from "dayjs/plugin/timezone"
import byteSize from "byte-size"

dayjs.extend(utc)
dayjs.extend(timezone)

const TIMEZONE = "Pacific/Auckland"

// SMART reports a lot of attributes and not every drive exposes the same ones,
// so charts are built from whichever of these the records actually carry.
// `axis` groups fields that share a scale onto a single y-axis, and the handful
// flagged `enabled` are the ones worth seeing before any toggling.
export const SSD_FIELDS = [
  { key: "remaining_life_pct", label: "Remaining Life (%)", axis: "percent", enabled: true },
  { key: "spare_blocks", label: "Spare Blocks (%)", axis: "percent" },
  { key: "host_writes_tib", label: "Host Writes (TiB)", axis: "tib", enabled: true },
  { key: "host_reads_tib", label: "Host Reads (TiB)", axis: "tib" },
  { key: "average_erase_count", label: "Avg Erase Count", axis: "erase", enabled: true },
  { key: "max_erase_count", label: "Max Erase Count", axis: "erase" },
  { key: "temperature_c", label: "Temperature (°C)", axis: "temperature" },
  { key: "power_on_hours", label: "Power On Hours", axis: "hours" },
  { key: "power_cycles", label: "Power Cycles", axis: "cycles" },
  { key: "total_bad_blocks", label: "Total Bad Blocks", axis: "blocks" },
  { key: "later_bad_blocks", label: "Later Bad Blocks", axis: "blocks" },
  { key: "pending_sectors", label: "Pending Sectors", axis: "blocks" },
  { key: "reported_uncorrectable", label: "Reported Uncorrectable", axis: "errors" },
  { key: "end_to_end_errors", label: "End To End Errors", axis: "errors" },
  { key: "sata_phy_errors", label: "SATA PHY Errors", axis: "errors" },
  { key: "program_fail_count", label: "Program Fail Count", axis: "errors" },
  { key: "erase_fail_count", label: "Erase Fail Count", axis: "errors" },
  { key: "host_writes_raw", label: "Host Writes (raw)", axis: "raw" },
  { key: "host_reads_raw", label: "Host Reads (raw)", axis: "raw" },
]

export const SSD_AXIS_LABELS = {
  percent: "%",
  tib: "TiB",
  erase: "Erase count",
  temperature: "°C",
  hours: "Hours",
  cycles: "Cycles",
  blocks: "Blocks",
  errors: "Errors",
  raw: "Raw count",
}

// Devices report SMART on their own cadence - hourly on some, daily on others -
// so a break in the line is judged against that device's usual interval rather
// than a fixed period, and never below this floor
export const SSD_GAP_MULTIPLE = 4
export const SSD_MIN_GAP_MS = 30 * 60 * 1000

export function formatSsdValue(value) {
  if (!Number.isFinite(value)) {
    return "-"
  }

  return value.toLocaleString("en-NZ", { maximumFractionDigits: 2 })
}

// Flattened device-event records in, one numeric point per snapshot out, oldest first
export function toSsdPoints(records) {
  return (records || [])
    .map(item => {
      const point = {
        timestamp: dayjs.tz(item.date_time, TIMEZONE).valueOf(),
        date_time: item.date_time,
        drive: item.serial_number || item.model || "Unknown drive",
        raw: item,
      }

      SSD_FIELDS.forEach(field => {
        const value = parseFloat(item[field.key])
        point[field.key] = Number.isFinite(value) ? value : null
      })

      return point
    })
    .filter(point => Number.isFinite(point.timestamp))
    .sort((a, b) => a.timestamp - b.timestamp)
}

export function summariseSsdDrive(point) {
  const latest = point.raw
  const capacity = parseFloat(latest.capacity_bytes)

  return {
    model: latest.model || latest.model_family || "Unknown",
    modelFamily: latest.model_family || "",
    serial: latest.serial_number || "Unknown",
    firmware: latest.firmware || "Unknown",
    capacity: Number.isFinite(capacity) ? byteSize(capacity).toString() : "-",
    remainingLife: point.remaining_life_pct,
    lastReported: latest.date_time,
  }
}

// A device can report more than one disk, so its snapshots are split per drive
// and each drive keeps its own line on a chart
export function groupSsdPointsByDrive(points) {
  const byDrive = new Map()

  points.forEach(point => {
    if (!byDrive.has(point.drive)) {
      byDrive.set(point.drive, [])
    }
    byDrive.get(point.drive).push(point)
  })

  return Array.from(byDrive.entries()).map(([id, drivePoints]) => ({
    id,
    points: drivePoints,
    summary: summariseSsdDrive(drivePoints[drivePoints.length - 1]),
  }))
}

// Only the attributes these points actually carry, so the toggles aren't mostly
// dead entries
export function availableSsdFields(points) {
  return SSD_FIELDS.filter(field => points.some(point => Number.isFinite(point[field.key])))
}

// How long a silence has to be before it counts as the drive having stopped
// reporting, taken from the typical interval between the snapshots being plotted
function breakThreshold(points) {
  const intervals = []
  for (let i = 1; i < points.length; i += 1) {
    intervals.push(points[i].timestamp - points[i - 1].timestamp)
  }

  if (!intervals.length) {
    return Infinity
  }

  intervals.sort((a, b) => a - b)
  const median = intervals[Math.floor(intervals.length / 2)]

  return Math.max(median * SSD_GAP_MULTIPLE, SSD_MIN_GAP_MS)
}

// Joins consecutive snapshots up so the trend is readable, breaking the line
// only where a drive genuinely stopped reporting
export function toSsdSeriesData(points, key) {
  // Only the snapshots carrying this attribute, so a field the odd record leaves
  // out doesn't fragment the line into loose dots
  const plotted = points.filter(point => Number.isFinite(point[key]))
  const threshold = breakThreshold(plotted)
  const data = []

  plotted.forEach((point, i) => {
    const prev = plotted[i - 1]
    if (prev && point.timestamp - prev.timestamp > threshold) {
      data.push([prev.timestamp + 1, null])
    }
    data.push([point.timestamp, point[key]])
  })

  return data
}
